import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faEdit, faTrash, faTicketAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons"

export default function CouponList() {
    const [coupons, setCoupons] = useState([])
    const [loading, setLoading] = useState(true)
    const [deleteLoading, setDeleteLoading] = useState(null)
    const [statusLoading, setStatusLoading] = useState(null)

    useEffect(() => {
        fetchCoupons()
    }, [])

    const fetchCoupons = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${baseUrl}/coupon/list?page=1&limit=1000`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            const listData =
                response.data?.data?.docs ||
                response.data?.data ||
                response.data?.docs ||
                response.data ||
                []

            setCoupons(Array.isArray(listData) ? listData : [])
        } catch (error) {
            console.error("Fetch coupons error:", error)
            toast.error("Failed to fetch coupons list")
        } finally {
            setLoading(false)
        }
    }

    const deleteCoupon = async (couponId) => {
        if (!window.confirm("Are you sure you want to delete this coupon?")) {
            return
        }

        try {
            setDeleteLoading(couponId)
            const response = await axios.delete(`${baseUrl}/coupon/delete/${couponId}`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.status === 200 || response.data?.status) {
                toast.success("Coupon deleted successfully")
                setCoupons((prev) => prev.filter((item) => item._id !== couponId))
            }
        } catch (error) {
            console.error("Delete error:", error)
            toast.error("Failed to delete coupon")
        } finally {
            setDeleteLoading(null)
        }
    }

    const toggleCouponStatus = async (couponId, currentStatus) => {
        try {
            setStatusLoading(couponId)
            const response = await axios.patch(`${baseUrl}/coupon/status/${couponId}`, {}, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.status === 200 || response.data?.status) {
                toast.success("Coupon status updated successfully")
                setCoupons((prev) =>
                    prev.map((item) =>
                        item._id === couponId
                            ? {
                                ...item,
                                status: currentStatus === "active" ? "inactive" : "active",
                            }
                            : item,
                    ),
                )
            }
        } catch (error) {
            console.error("Status toggle error:", error)
            toast.error("Failed to update coupon status")
        } finally {
            setStatusLoading(null)
        }
    }

    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A"

        const date = new Date(dateString)

        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    const isCouponExpired = (endDate) => {
        if (!endDate) return false
        return new Date() > new Date(endDate)
    }

    const getDiscountLabel = (coupon) => {
        if (coupon.discountType === "flat") {
            return `Rs. ${coupon.discountValue} OFF`
        }

        return `${coupon.discountValue}% OFF`
    }

    const getApplicabilityLabel = (coupon) => {
        const couponFor = coupon.couponFor || "all"
        const applicableOn = coupon.applicableOn || "all"
        const selectedCount = coupon.packages?.length || 0

        if (couponFor === "all") {
            return "All Packages & Daily Mock"
        }

        if (couponFor === "daily_mock") {
            return applicableOn === "selected"
                ? `${selectedCount} Selected Daily Mock Categories`
                : "All Daily Mock Categories"
        }

        return applicableOn === "selected"
            ? `${selectedCount} Selected Packages`
            : "All Packages"
    }

    if (loading) {
        return (
            <section className="main-sec">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading coupons...</p>
                </div>
            </section>
        )
    }

    return (
        <>
            <style>{`
                .coupon-code-badge {
                    display: inline-block;
                    padding: 0.4rem 0.8rem;
                    border: 2px dashed #7436A9;
                    border-radius: 6px;
                    color: #7436A9;
                    background-color: #f7f3fd;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }
                .status-active {
                    color: #2e7d32;
                    background-color: #e8f5e9;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .status-inactive {
                    color: #c62828;
                    background-color: #ffebee;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .status-expired {
                    color: #ef6c00;
                    background-color: #fff3e0;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .action-btn {
                    padding: 0.35rem 0.75rem;
                    margin: 0 0.25rem;
                    border-radius: 5px;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .action-btn:hover {
                    transform: translateY(-1px);
                }
            `}</style>

            <section className="main-sec">
                <div className="container">
                    <div className="row align-items-center mb-4">
                        <div className="col-md-6">
                            <div className="dashboard-title">
                                <h4 className="dash-head">
                                    <FontAwesomeIcon icon={faTicketAlt} className="me-2 text-primary" />
                                    Coupons List
                                </h4>
                            </div>
                            <div className="custom-bredcump">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                                        <li className="breadcrumb-item active" aria-current="page">Coupon Management</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <Link to="/coupons/create" className="btn btn-for-add text-white">
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Create Coupon
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="cards">
                                <div className="table-responsive custom-table">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "50px" }}>SN</th>
                                                <th>Coupon Code</th>
                                                <th>Title & Description</th>
                                                <th>Discount Details</th>
                                                <th>Applicability</th>
                                                <th>Validity Range</th>
                                                <th>Status</th>
                                                <th className="text-center" style={{ width: "120px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {coupons.length > 0 ? (
                                                coupons.map((coupon, i) => {
                                                    const expired = isCouponExpired(coupon.validTill)

                                                    return (
                                                        <tr key={coupon._id}>
                                                            <td>{i + 1}</td>
                                                            <td>
                                                                <span className="coupon-code-badge">
                                                                    {coupon.couponCode}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold text-dark">{coupon.title}</div>
                                                                <small className="text-muted d-block" style={{ maxWidth: "250px" }}>
                                                                    {coupon.description || "No description provided."}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                <div className="fw-bold text-primary">
                                                                    {getDiscountLabel(coupon)}
                                                                </div>
                                                                {coupon.maxDiscount && (
                                                                    <small className="text-muted">
                                                                        Max: Rs. {coupon.maxDiscount}
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={
                                                                        coupon.applicableOn === "selected"
                                                                            ? "badge bg-info text-dark"
                                                                            : "badge bg-secondary"
                                                                    }
                                                                >
                                                                    {getApplicabilityLabel(coupon)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <small className="d-block text-muted">
                                                                    <strong>From:</strong> {formatDateTime(coupon.validFrom)}
                                                                </small>
                                                                <small className="d-block text-muted">
                                                                    <strong>Till:</strong> {formatDateTime(coupon.validTill)}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                {statusLoading === coupon._id ? (
                                                                    <div className="text-center">
                                                                        <span className="spinner-border spinner-border-sm text-primary"></span>
                                                                    </div>
                                                                ) : expired ? (
                                                                    <span className="status-expired" style={{ cursor: "not-allowed" }} title="Coupon has expired">Expired</span>
                                                                ) : (
                                                                    <span
                                                                        className={coupon.status === "active" ? "status-active" : "status-inactive"}
                                                                        onClick={() => toggleCouponStatus(coupon._id, coupon.status)}
                                                                        style={{ cursor: "pointer", userSelect: "none" }}
                                                                        title="Click to toggle status"
                                                                    >
                                                                        {coupon.status === "active" ? "Active" : "Inactive"}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex justify-content-center">
                                                                    <Link
                                                                        to={`/coupons/edit/${coupon._id}`}
                                                                        className="btn btn-outline-primary action-btn me-2"
                                                                        title="Edit"
                                                                    >
                                                                        <FontAwesomeIcon icon={faEdit} />
                                                                    </Link>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger action-btn"
                                                                        onClick={() => deleteCoupon(coupon._id)}
                                                                        disabled={deleteLoading === coupon._id}
                                                                        title="Delete"
                                                                    >
                                                                        {deleteLoading === coupon._id ? (
                                                                            <span className="spinner-border spinner-border-sm"></span>
                                                                        ) : (
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4 text-muted">
                                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                                        No coupons found. Click Create Coupon to add one.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
